// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { handleAPIError } from '@/api/error-utils';
import { useCurrentUser } from '@/authentication/hooks/use-current-user';
import { PageContent } from '@/components/page-content';
import { SegmentedControl } from '@/components/segmented-control';
import { ErrorRounded } from '@/constants/hardfin-standard-icons';
import { useResizeObserver } from '@/hooks/use-resize-observer';
import { ProjectReportTable } from '@/report/components/project-report-table';
import { ReportBreadcrumbs } from '@/report/components/report-breadcrumbs';
import { ReportDateRangeInput } from '@/report/components/report-date-range-input';
import { useProjectReport } from '@/report/hooks/use-project-report';
import { getReportFileName } from '@/report/utilities/get-report-file-name';
import { getToday } from '@/utilities/date-time';
import { Box, Stack, Tooltip, Typography } from '@mui/material';
import { DateRange } from '@mui/x-date-pickers-pro';
import { createFileRoute } from '@tanstack/react-router';
import { DateTime } from 'luxon';
import { useCallback, useRef } from 'react';
import { z } from 'zod';

const REPORT_TITLE = 'Active projects report';

const groupSchema = z.enum(['customer']);

type Group = z.infer<typeof groupSchema>;

const GROUP_OPTIONS = [
  {
    label: 'Customer',
    value: groupSchema.options[0],
  },
];

const searchSchema = z.object({
  start: z.string().optional(),
  end: z.string().optional(),
  group: z.array(groupSchema).optional(),
});

export const Route = createFileRoute('/_app/reports/active-projects')({
  component: ActiveProjectsReportRoute,
  validateSearch: searchSchema,
});

function ActiveProjectsReportRoute() {
  const search = Route.useSearch();
  const navigate = Route.useNavigate();

  const endDate = search.end
    ? DateTime.fromISO(search.end)
    : getToday().minus({ days: 1 });

  const startDate = search.start
    ? DateTime.fromISO(search.start)
    : endDate.minus({ days: 31 });

  const { user } = useCurrentUser();
  const {
    error,
    isError,
    interval,
    data = [],
  } = useProjectReport({
    type: 'active',
    start: startDate.toISODate(),
    end: endDate.toISODate(),
  });

  const containerRef = useRef<HTMLDivElement>(null);
  const containerSize = useResizeObserver(containerRef);

  const group = search.group ?? ['customer'];
  const isDateRangeError =
    isError &&
    error !== null &&
    handleAPIError(error).errorType === 'date_range_exceeded';

  const handleChangeDateRange = (dateRange: DateRange<DateTime>) => {
    const start = dateRange[0]?.toISODate();
    const end = dateRange[1]?.toISODate();

    navigate({ search: { ...search, start, end } });
  };

  const handleChangeGroup = (group: Group[]) => {
    navigate({ search: { ...search, group } });
  };

  const getFileName = useCallback(() => {
    return getReportFileName(
      'active-projects-report',
      user.organization.timezone
    );
  }, [user.organization.timezone]);

  return (
    <>
      <PageContent
        sx={{
          gap: 2,
          flex: 1,
          display: 'grid',
          maxHeight: '100%',
          gridTemplateRows: 'repeat(2, auto) 1fr',
          gridTemplateColumns: '1fr 1fr',
        }}
      >
        <Stack gridColumn="span 2">
          <ReportBreadcrumbs title={REPORT_TITLE} />
          <Typography variant="h5">{REPORT_TITLE}</Typography>
          <Typography variant="body2" color="text.secondary">
            Includes total number of units planned for projects that were active
            each day
          </Typography>
        </Stack>
        <SegmentedControl
          multiple
          label="Group by"
          value={group}
          options={GROUP_OPTIONS}
          onChange={handleChangeGroup}
        />
        <ReportDateRangeInput
          interval={interval}
          timezone={user.organization.timezone}
          onChange={handleChangeDateRange}
        >
          {isDateRangeError && (
            <Tooltip
              arrow
              title="The maximum date range for this report is three years"
            >
              <ErrorRounded color="error" fontSize="small" />
            </Tooltip>
          )}
        </ReportDateRangeInput>
        <Box ref={containerRef} gridColumn="span 2">
          <ProjectReportTable
            data={data}
            group={group}
            interval={interval}
            maximumHeight={containerSize.height}
            getFileName={getFileName}
          />
        </Box>
      </PageContent>
    </>
  );
}
